import { useState, useEffect } from "react";
import cardBackgroundLight from "../../assets/images/card_background.svg";
import cardBackgroundDark from "../../assets/images/card_background_dark.svg";
import { ReactComponent as VectorArrowIcon } from "../../assets/images/vector_arrow.svg";
import { replaceTemplateKeysWithPills } from "../../utils/templatesTag.js";
import { sanitizeHtml } from "../../utils/sanitizeHtml";
import LoadingSpinner from "../spinner/LoadingSpinner";
import styles from "./TemplateFileCard.module.css";

function getCurrentTheme() {
  return document.documentElement.getAttribute("data-theme") === "dark"
    ? "dark"
    : "light";
}

function TemplateFileCard({
  file,
  theme,
  onClick,
  draggable = false,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  dropPosition = null,
  isUpdating = false,
}) {
  const [internalTheme, setInternalTheme] = useState(getCurrentTheme);

  const resolvedTheme = theme ?? internalTheme;
  const backgroundImage =
    resolvedTheme === "dark" ? cardBackgroundDark : cardBackgroundLight;

  useEffect(() => {
    if (theme) return undefined;

    const observer = new MutationObserver(() => {
      setInternalTheme(getCurrentTheme());
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, [theme]);

  const safeHtml = sanitizeHtml(
    replaceTemplateKeysWithPills(file?.text || "", true),
  );

  const isInactive = file?.is_active === false;

  return (
    <div
      draggable={draggable && !isUpdating}
      aria-busy={isUpdating}
      onDragStart={isUpdating ? undefined : onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      style={{
        position: "relative",
        display: "flex",
        width: "231px",
        height: "347px",
        padding: "20px 24px 16px 24px",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "16px",
        flexShrink: 0,
        borderRadius: "24px",
        background: "var(--surface-secondary)",
        fontFamily: 'var(--font-body, "DM Sans")',
        fontSize: "14px",
        fontStyle: "normal",
        fontWeight: "500",
        lineHeight: "normal",
        letterSpacing: "-0.42px",
        cursor: isUpdating ? "wait" : draggable ? "grab" : "default",
        opacity: isInactive ? 0.55 : 1,
      }}
    >
      {isUpdating && (
        <div
          data-testid="template-file-card-spinner"
          className={styles.updatingOverlay}
        >
          <LoadingSpinner size={40} />
        </div>
      )}

      {dropPosition && (
        <div
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: dropPosition === "before" ? -12 : "auto",
            right: dropPosition === "after" ? -12 : "auto",
            width: 4,
            borderRadius: 999,
            background: "var(--color-brand-600)",
            boxShadow: "0 0 12px var(--color-brand-600)",
            zIndex: 10,
            pointerEvents: "none",
          }}
        />
      )}

      <div
        style={{
          width: "100%",
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 6,
          WebkitBoxOrient: "vertical",
        }}
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />

      <div
        style={{
          marginTop: "auto",
          position: "relative",
          width: "100%",
        }}
      >
        <div
          role="button"
          tabIndex={0}
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: 48,
            height: 48,
            borderRadius: "var(--br-100)",
            backgroundColor: "var(--surface-tertiary)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxSizing: "border-box",
          }}
          onClick={() => {
            if (!isUpdating) onClick(file);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              if (!isUpdating) onClick(file);
            }
          }}
        >
          <VectorArrowIcon
            style={{
              width: 13.5,
              position: "relative",
              borderRadius: 0.25,
              height: 13.5,
            }}
          />
        </div>

        <img alt="" src={backgroundImage} />

        <div
          style={{
            position: "absolute",
            height: "78.57%",
            width: "80%",
            top: "1%",
            right: "6.54%",
            bottom: "11.51%",
            left: "6.83%",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <p
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              width: "75%",
              textOverflow: "ellipsis",
              lineHeight: "1.2em",
              maxHeight: "2.4em",
              flexShrink: 0,
            }}
          >
            {file?.name || "Sin asunto"}
          </p>

          <p
            style={{
              textAlign: "right",
              marginTop: 20,
              color: "var(--text-tertiary)",
            }}
          >
            {isInactive ? "Inactivo" : `#${file?.orden ?? ""}`}
          </p>
        </div>
      </div>
    </div>
  );
}

export default TemplateFileCard;
